const getService = (context, name) => {
    const service = context.services && context.services[name]

    if (typeof service !== 'function') {
        throw new Error(`Product concern substances requires services.${name}()`)
    }

    return service
}

const replaceArray = (target, values) => {
    target.splice(0, target.length, ...(values || []))
}

const hasListQuestionState = (context, list, state) => {
    const questionStates = context.product && Array.isArray(context.product.substanceListQuestionStates)
        ? context.product.substanceListQuestionStates
        : []
    const currentState = questionStates.find(item => String(item.list) === String(list.id))

    return currentState && currentState.state === state
}

const createProductConcernSubstancesController = ({
    context,
    setDirty,
    setSaving,
    publishEffect,
    reportError,
}) => {
    let validation = { isValid: false, errors: [] }
    let substancesDirty = false
    let cardDirty = false
    let disposed = false

    const publishCommitted = (type, scopes) => publishEffect({ type, phase: 'committed', scopes })

    const runSaving = async operation => {
        setSaving(true)
        try {
            const result = await operation()
            reportError(null)
            return result
        } catch (error) {
            reportError(error)
            throw error
        } finally {
            setSaving(false)
        }
    }

    const refreshSubstances = async () => {
        const { included = [], excluded = [] } = await getService(context, 'refreshConcernSubstances')()
        replaceArray(context.includedSubstances, included)
        replaceArray(context.excludedSubstances, excluded)
        await getService(context, 'refreshSubstanceState')()
    }

    const refreshComponents = async () => {
        const components = await getService(context, 'getComponentConcernSubstances')()
        replaceArray(context.componentConcernSubstances, components)
    }

    const saveSubstances = async () => {
        if (!substancesDirty) {
            return
        }
        await getService(context, 'saveSubstances')()
        substancesDirty = false
        await refreshSubstances()
        await publishCommitted('product.concern-substances.changed', ['product.concern-substances'])
    }

    const saveCard = async () => {
        if (!cardDirty) {
            return
        }
        await getService(context, 'saveProductCard')()
        cardDirty = false
        await publishCommitted('product.card.changed', ['product.card'])
    }

    const markDirty = channel => {
        const wasDirty = substancesDirty || cardDirty

        if (channel === 'substances') {
            substancesDirty = true
        } else {
            cardDirty = true
        }

        if (!wasDirty) {
            setDirty(true)
        }
    }

    const flushChanges = async () => {
        await saveSubstances()
        await saveCard()
        setDirty(substancesDirty || cardDirty)
    }

    return Object.freeze({
        async load() {
            await Promise.all([
                refreshComponents(),
                getService(context, 'refreshProductWeightRequirement')().catch(() => false),
            ])
        },
        setValidation(nextValidation) {
            validation = nextValidation
        },
        validate() {
            return Promise.resolve(validation)
        },
        async handleAction(action) {
            if (disposed) {
                return
            }

            const payload = action.payload || {}
            switch (action.type) {
            case 'forward':
                await getService(context, 'forwardRequest')()
                return
            case 'substance.include':
                getService(context, 'includeConcernSubstance')(payload.substance, payload.substanceListIds)
                markDirty('substances')
                return
            case 'substance.exclude':
                getService(context, 'excludeConcernSubstance')(payload.substance)
                markDirty('substances')
                return
            case 'substances.exclude':
                payload.substances.forEach(substance => {
                    getService(context, 'excludeConcernSubstance')(substance)
                })
                markDirty('substances')
                return
            case 'substance.undefined':
                getService(context, 'makeConcernSubstanceUndefined')(payload.substance)
                markDirty('substances')
                if (payload.save) {
                    await runSaving(flushChanges)
                    if (typeof payload.completionCallback === 'function') {
                        payload.completionCallback()
                    }
                }
                return
            case 'substance-properties.change':
                getService(context, 'updateConcernSubstanceProperties')(payload.substance, payload.properties)
                markDirty('substances')
                await runSaving(flushChanges)
                if (typeof payload.completionCallback === 'function') {
                    payload.completionCallback()
                }
                return
            case 'information-owner.change':
                await runSaving(() => getService(context, 'changeInformationOwner')(payload.informationOwner))
                await publishCommitted('request.details.changed', ['request.details'])
                return
            case 'has-concern-substances.change':
                getService(context, 'setHasConcernSubstances')(payload.value)
                markDirty('card')
                await runSaving(flushChanges)
                return
            case 'list-question.update':
                if (hasListQuestionState(context, payload.list, payload.state)) {
                    return
                }
                getService(context, 'updateListQuestionState')(payload.list, payload.state)
                markDirty('card')
                return
            case 'characteristics.change':
                getService(context, 'setProductCharacteristics')(payload.characteristics)
                markDirty('card')
                await runSaving(flushChanges)
                await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
                return
            case 'selector.closed':
                await runSaving(flushChanges)
                await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
                return
            case 'components.changed':
                await runSaving(async () => {
                    await refreshComponents()
                    await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
                })
                await publishCommitted(
                    'product.component-concern-substances.changed',
                    ['product.component-concern-substances'],
                )
                return
            case 'component-substance.saved':
                await getService(context, 'refreshProductWeightRequirement')().catch(() => false)
                return
            default:
                throw new Error(`Unsupported product concern substances action "${action.type}"`)
            }
        },
        flush() {
            return runSaving(flushChanges)
        },
        async handleEffect(effect) {
            if (['request.details.changed', 'product.card.changed'].includes(effect.type)) {
                await refreshComponents()
            }
        },
        dispose() {
            disposed = true
        },
    })
}

export { createProductConcernSubstancesController }
export default createProductConcernSubstancesController
